import { ApiError, apiErrorCode, type PurposeDef } from "./api.ts";
import { jobsSentence, modelName } from "./models.ts";

// Refusals from the models API, worded on the client with the job and model
// names the page shows. A code this file doesn't know keeps the server's text.

export const ADDRESS_NOT_ALLOWED =
  "Boo runs in the cloud, so it can't reach addresses on your own computer or private network. Use an address that's reachable from the internet, for example through a secure tunnel.";

export type ModelErrorContext = {
  defs: Array<Pick<PurposeDef, "id" | "label">>;
  // The model the request was about, when there was one.
  slug?: string;
  // The display name of your own model service the request was about.
  source?: string;
  isCustom?: (slug: string) => boolean;
};

export function describeModelError(err: unknown, ctx: ModelErrorContext): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : "Something went wrong. Try again.";
  const body = (err.body ?? {}) as { purposes?: unknown; model?: unknown };
  const purposes = Array.isArray(body.purposes) ? body.purposes.filter((p): p is string => typeof p === "string") : [];
  const jobs = purposes.length ? jobsSentence(purposes, ctx.defs) : "that job";
  const slug = ctx.slug ?? (typeof body.model === "string" ? body.model : undefined);
  const model = slug ? modelName(slug, { custom: ctx.isCustom?.(slug) ?? false }) : null;
  switch (apiErrorCode(err)) {
    case "model_is_default":
      if (model) return `${model} is the default for ${jobs}. Choose another model for ${jobs} before turning it off.`;
      if (ctx.source) return `${ctx.source} is the default for ${jobs}. Choose another model for ${jobs} first.`;
      return `That model is the default for ${jobs}. Choose another model for ${jobs} first.`;
    case "model_is_builtin_default":
      if (model) return `Boos use ${model} for ${jobs} when nothing else is chosen. Choose a model for ${jobs} before turning it off.`;
      return `The built-in model for ${jobs} is turned off. Choose a model for ${jobs}.`;
    case "model_disabled":
      return model
        ? `${model} is turned off for your organization. Choose another model.`
        : "One of these models is turned off for your organization. Choose another one.";
    case "custom_not_supported":
      return purposes.length
        ? `${jobs} uses built-in models only. Choose a built-in model.`
        : "That job uses built-in models only. Choose a built-in model.";
    case "brain_outdated":
      return "This Boo needs an update before it can do this. It updates automatically, usually within a few days.";
    case "no_running_boo":
      return "Start one of your Boos to check the connection. You can save without checking.";
    case "address_not_allowed":
      return ADDRESS_NOT_ALLOWED;
    case "key_required":
      return "Enter the access key again for the new address.";
    case "name_in_use":
      return "A Boo already uses this ID for another address. Choose another ID under Advanced.";
    case "too_many_endpoints":
      return "You can connect up to 10 of your own model services.";
    case "probe_expired":
      return "This check expired. Run it again.";
    case "rate_limited":
      return "Too many checks in a short time. Try again in a few minutes.";
    default:
      return err.message;
  }
}
